import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import {
    BarChart3,
    Sparkles,
    MessageSquare,
    Users as UsersIcon,
    Image as ImageIcon,
    HardDrive,
    Wallet,
    TrendingUp,
    Palette,
} from "lucide-react";

const DAY_RANGES = [
    { id: "7d", label: "Últimos 7 días", type: "days", days: 7 },
    { id: "14d", label: "Últimos 14 días", type: "days", days: 14 },
    { id: "30d", label: "Últimos 30 días", type: "days", days: 30 },
    { id: "90d", label: "Últimos 90 días", type: "days", days: 90 },
];
const ALL_RANGE = { id: "all", label: "Desde el inicio", type: "all" };
const MONTHS_ES = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
// Build the last 12 calendar months as selectable ranges (e.g. "agosto 2025").
function buildMonthOptions() {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const y = d.getFullYear();
        const m = d.getMonth();
        out.push({
            id: `${y}-${String(m + 1).padStart(2, "0")}`,
            label: `${MONTHS_ES[m]} ${y}`,
            type: "month",
            year: y,
            month: m,
        });
    }
    return out;
}
// Translate a range option into the query string for daily-by-user.
function rangeQuery(opt) {
    if (!opt) return "days=30";
    if (opt.type === "days") return `days=${opt.days}`;
    if (opt.type === "all") return "all=true";
    if (opt.type === "month") {
        const start = `${opt.year}-${String(opt.month + 1).padStart(2, "0")}-01`;
        const last = new Date(opt.year, opt.month + 1, 0).getDate();
        const end = `${opt.year}-${String(opt.month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
        return `start=${start}&end=${end}`;
    }
    return "days=30";
}

const fmtCop = (v) =>
    `$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(v || 0)}`;
const fmtBytes = (b) => {
    const gb = (b || 0) / 1024 ** 3;
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    return `${((b || 0) / 1024 ** 2).toFixed(1)} MB`;
};
// Month helpers for the monthly-aggregation view (ranges > 30 days).
const monthKeyOf = (iso) => String(iso).slice(0, 7); // "YYYY-MM"
const monthLabelShort = (key) => {
    const [y, m] = String(key).split("-");
    return `${(MONTHS_ES[Number(m) - 1] || "").slice(0, 3)} ${String(y).slice(2)}`;
};

export default function Stats() {
    const [range, setRange] = useState("30d");
    const [global, setGlobal] = useState(null);
    const [daily, setDaily] = useState(null);
    const [consumption, setConsumption] = useState(null);
    const [storage, setStorage] = useState(null);
    // unit toggle — "qty" shows counts, "money" shows COP costs. The
    // toggle drives every chart, KPI and leaderboard on the page so the
    // user can flip between volume and spend with one click.
    const [unit, setUnit] = useState("qty");

    const monthOptions = useMemo(() => buildMonthOptions(), []);
    const rangeOptions = useMemo(
        () => [...DAY_RANGES, ALL_RANGE, ...monthOptions],
        [monthOptions],
    );
    const rangeDef =
        rangeOptions.find((r) => r.id === range) || DAY_RANGES[2];
    const rangeQ = rangeQuery(rangeDef);

    useEffect(() => {
        api.get(`/admin/stats?${rangeQ}`).then((r) => setGlobal(r.data)).catch(() => {});
        api.get("/credits/consumption").then((r) => setConsumption(r.data)).catch(() => {});
        api.get("/storage").then((r) => setStorage(r.data)).catch(() => {});
    }, [rangeQ]);

    useEffect(() => {
        setDaily(null);
        api.get(`/admin/stats/daily-by-user?${rangeQ}`)
            .then((r) => setDaily(r.data))
            .catch(() => {});
    }, [rangeQ]);

    const isMoney = unit === "money";
    // When the selected window spans more than 30 days we aggregate the
    // chart + heatmap by MONTH (one bar/column per month) instead of per
    // day, so the visualization stays readable and never overflows.
    const granularity = (daily?.days?.length || 0) > 30 ? "month" : "day";
    const valueFmt = (v) =>
        isMoney
            ? fmtCop(v)
            : Number(v || 0).toLocaleString("es-CO");

    // Daily/monthly series split image vs text — feeds the dual-bar chart.
    // When unit is "money" we sum cost_cop instead of counts. When the
    // range is > 30 days we roll the per-day buckets up into per-month bars.
    const dailySeries = useMemo(() => {
        if (!daily) return [];
        const dayBuckets = {};
        (daily.rows || []).forEach((r) => {
            r.series.forEach((s) => {
                const b = dayBuckets[s.date] || { image: 0, text: 0 };
                if (isMoney) {
                    b.image += s.image_cost_cop || 0;
                    b.text += s.text_cost_cop || 0;
                } else {
                    b.image += s.image_count || 0;
                    b.text += s.text_count || 0;
                }
                dayBuckets[s.date] = b;
            });
        });
        if (granularity === "month") {
            const mb = {};
            const keys = [];
            (daily.days || []).forEach((d) => {
                const k = monthKeyOf(d);
                if (!(k in mb)) {
                    mb[k] = { image: 0, text: 0 };
                    keys.push(k);
                }
                mb[k].image += dayBuckets[d]?.image || 0;
                mb[k].text += dayBuckets[d]?.text || 0;
            });
            return keys.map((k) => ({
                date: `${k}-01`,
                label: monthLabelShort(k),
                image: mb[k].image,
                text: mb[k].text,
            }));
        }
        return (daily.days || []).map((d) => ({
            date: d,
            image: dayBuckets[d]?.image || 0,
            text: dayBuckets[d]?.text || 0,
        }));
    }, [daily, isMoney, granularity]);

    const rangeTotals = useMemo(() => {
        const out = { image: 0, text: 0 };
        dailySeries.forEach((s) => {
            out.image += s.image;
            out.text += s.text;
        });
        return out;
    }, [dailySeries]);

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-7xl mx-auto px-10 py-14 space-y-10">
                    <header className="flex items-end justify-between gap-6 flex-wrap">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2 flex items-center gap-2">
                                <BarChart3 className="w-3 h-3" />
                                Rendimiento
                            </p>
                            <h1 className="font-display text-5xl text-fog-100">
                                Estadísticas
                            </h1>
                            <p className="text-fog-400 text-sm mt-2 max-w-xl">
                                Análisis de consumo, generación y uso por marca y usuario.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div
                                data-testid="stats-unit-toggle"
                                className="inline-flex rounded-xl bg-ink-900 border hairline border-ink-600 p-1"
                            >
                                {[
                                    { id: "qty", label: "Cantidad" },
                                    { id: "money", label: "Dinero" },
                                ].map((opt) => (
                                    <button
                                        key={opt.id}
                                        data-testid={`stats-unit-${opt.id}`}
                                        onClick={() => setUnit(opt.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest transition-colors ${
                                            unit === opt.id
                                                ? "bg-periwinkle-500 text-ink-950"
                                                : "text-fog-400 hover:text-fog-100"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <select
                                data-testid="stats-range-select"
                                value={range}
                                onChange={(e) => setRange(e.target.value)}
                                className="rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 text-sm px-4 py-2.5 outline-none focus:border-periwinkle-500"
                            >
                                <optgroup label="Rangos">
                                    {DAY_RANGES.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Histórico">
                                    <option value={ALL_RANGE.id}>
                                        {ALL_RANGE.label}
                                    </option>
                                </optgroup>
                                <optgroup label="Por mes">
                                    {monthOptions.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.label}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>
                    </header>

                    {/* KPI grid */}
                    <section
                        data-testid="stats-kpi-grid"
                        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
                    >
                        <KPI
                            icon={<Sparkles className="w-5 h-5" />}
                            label={`Consumo · ${rangeDef.label.toLowerCase()}`}
                            value={valueFmt(rangeTotals.image + rangeTotals.text)}
                            sublabel={
                                isMoney
                                    ? "imagen + texto en el periodo"
                                    : `${rangeTotals.image.toLocaleString("es-CO")} imgs · ${rangeTotals.text.toLocaleString("es-CO")} textos`
                            }
                            color="#a5a6ff"
                        />
                        <KPI
                            icon={<ImageIcon className="w-5 h-5" />}
                            label={isMoney ? "Gasto imágenes" : "Imágenes"}
                            value={
                                isMoney
                                    ? fmtCop(global?.totals?.image?.cost_cop || 0)
                                    : (global?.total_images || 0).toLocaleString("es-CO")
                            }
                            sublabel={rangeDef.label.toLowerCase()}
                            color="#8fe7d0"
                        />
                        <KPI
                            icon={<MessageSquare className="w-5 h-5" />}
                            label={isMoney ? "Gasto textos" : "Textos"}
                            value={
                                isMoney
                                    ? fmtCop(global?.totals?.text?.cost_cop || 0)
                                    : (global?.total_texts || 0).toLocaleString("es-CO")
                            }
                            sublabel={rangeDef.label.toLowerCase()}
                            color="#ffbe7b"
                        />
                        <KPI
                            icon={<UsersIcon className="w-5 h-5" />}
                            label="Usuarios activos"
                            value={(
                                global?.active_users ?? 0
                            ).toLocaleString("es-CO")}
                            sublabel="activos"
                            color="#ff9ec7"
                        />
                    </section>

                    <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {consumption && (
                            <BigCard
                                icon={<Wallet className="w-5 h-5" />}
                                color="#a5a6ff"
                                title="Presupuesto (mes)"
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className="font-display text-3xl text-fog-100">
                                        {fmtCop(consumption.cop_month)}
                                    </span>
                                    <span className="font-mono text-[11px] text-fog-400">
                                        / {fmtCop(consumption.monthly_budget_cop)}
                                    </span>
                                </div>
                                <Progress
                                    pct={consumption.percent_used_month}
                                    over={consumption.over_budget}
                                />
                                <p className="font-mono text-[10px] text-fog-400 mt-2 uppercase tracking-widest">
                                    {consumption.images_month} imgs · costo/img{" "}
                                    {fmtCop(consumption.cost_per_image_cop)}
                                </p>
                            </BigCard>
                        )}
                        {storage && (
                            <BigCard
                                icon={<HardDrive className="w-5 h-5" />}
                                color="#8fe7d0"
                                title="Almacenamiento"
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className="font-display text-3xl text-fog-100">
                                        {storage.used_gb.toFixed(2)} GB
                                    </span>
                                    <span className="font-mono text-[11px] text-fog-400">
                                        / {storage.limit_gb.toFixed(0)} GB
                                    </span>
                                </div>
                                <Progress pct={storage.percent_used} />
                                <p className="font-mono text-[10px] text-fog-400 mt-2 uppercase tracking-widest">
                                    {storage.files_count} archivos
                                </p>
                            </BigCard>
                        )}
                        <BigCard
                            icon={<Palette className="w-5 h-5" />}
                            color="#ff9ec7"
                            title="Marcas activas"
                        >
                            <div className="flex items-baseline gap-2">
                                <span className="font-display text-3xl text-fog-100">
                                    {global?.total_brands || 0}
                                </span>
                            </div>
                            <p className="font-mono text-[10px] text-fog-400 mt-5 uppercase tracking-widest">
                                {(global?.total_references || 0).toLocaleString("es-CO")} referencias cargadas
                            </p>
                        </BigCard>
                    </section>

                    {/* Daily totals dual bar */}
                    <section>
                        <div className="flex items-baseline justify-between mb-4">
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase flex items-center gap-2">
                                <TrendingUp className="w-3 h-3" />
                                Consumo por {granularity === "month" ? "mes" : "día"} · {isMoney ? "dinero (COP)" : "cantidad"}
                            </p>
                            <p className="font-mono text-[10px] text-fog-500 flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400" />
                                    imagen {valueFmt(rangeTotals.image)}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="inline-block w-2 h-2 rounded-sm bg-periwinkle-400" />
                                    texto {valueFmt(rangeTotals.text)}
                                </span>
                            </p>
                        </div>
                        <StackedBarChart data={dailySeries} isMoney={isMoney} granularity={granularity} />
                    </section>

                    {/* Heatmap per user */}
                    {daily && daily.rows && daily.rows.length > 0 && (
                        <section>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                                <UsersIcon className="w-3 h-3" />
                                Detalle por usuario {isMoney ? "(dinero)" : "(cantidad)"}
                            </p>
                            <UserHeatmap data={daily} isMoney={isMoney} granularity={granularity} />
                        </section>
                    )}

                    {/* Leaderboards */}
                    {global && (
                        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <Leaderboard
                                title={`Top usuarios · ${rangeDef.label.toLowerCase()}`}
                                rows={global.per_user || []}
                                labelKey="user_name"
                                color="#a5a6ff"
                                testid="stats-top-users"
                                isMoney={isMoney}
                            />
                            <Leaderboard
                                title={`Top marcas · ${rangeDef.label.toLowerCase()}`}
                                rows={global.per_brand || []}
                                labelKey="brand_name"
                                color="#ff9ec7"
                                testid="stats-top-brands"
                                isMoney={isMoney}
                            />
                            <Leaderboard
                                title="Top modelos"
                                rows={global.per_model || []}
                                labelKey="name"
                                color="#8fe7d0"
                                testid="stats-top-models"
                                isMoney={isMoney}
                            />
                        </section>
                    )}
                </div>
            </main>
        </div>
    );
}

function KPI({ icon, label, value, sublabel, color }) {
    return (
        <div
            data-testid={`stats-kpi-${label.toLowerCase().replace(/\s/g, "-").split("·")[0]}`}
            className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5 flex flex-col gap-3"
        >
            <div className="flex items-center gap-2">
                <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: `${color}22`, color }}
                >
                    {icon}
                </div>
            </div>
            <div>
                <p className="font-display text-3xl text-fog-100">{value}</p>
                <p className="font-mono text-[10px] text-fog-400 uppercase tracking-widest mt-1 line-clamp-1">
                    {label}
                </p>
                {sublabel && (
                    <p className="font-mono text-[9px] text-fog-500 mt-0.5">
                        {sublabel}
                    </p>
                )}
            </div>
        </div>
    );
}

function BigCard({ icon, color, title, children }) {
    return (
        <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5">
            <div className="flex items-center gap-2 mb-3">
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `${color}22`, color }}
                >
                    {icon}
                </div>
                <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                    {title}
                </p>
            </div>
            {children}
        </div>
    );
}

function Progress({ pct, over }) {
    const clamped = Math.min(Math.max(pct || 0, 0), 100);
    return (
        <div className="mt-3 h-1.5 rounded-full bg-ink-700 overflow-hidden">
            <div
                className="h-full transition-all duration-500"
                style={{
                    width: `${clamped}%`,
                    background: over
                        ? "#ff6b8b"
                        : clamped < 60
                          ? "#8fe7d0"
                          : clamped < 85
                            ? "#ffbe7b"
                            : "#ff6b8b",
                }}
            />
        </div>
    );
}

function BarChart({ data }) {
    const max = Math.max(1, ...data.map((d) => d.count));
    const fmtDay = (iso) => {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit" });
    };
    return (
        <div
            data-testid="stats-bar-chart"
            className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5"
        >
            <div
                className="flex items-stretch gap-1.5"
                style={{ height: 160 }}
            >
                {data.map((d) => {
                    const h = Math.round((d.count / max) * 100);
                    return (
                        <div
                            key={d.date}
                            title={`${fmtDay(d.date)} · ${d.count} imágenes`}
                            className="group flex-1 min-w-[8px] flex flex-col items-center justify-end relative h-full"
                        >
                            <div
                                className={`w-full rounded-t transition-all duration-300 ${
                                    d.count > 0
                                        ? "bg-periwinkle-500 group-hover:bg-periwinkle-400"
                                        : "bg-ink-700"
                                }`}
                                style={{
                                    height: d.count > 0 ? `${Math.max(h, 4)}%` : "2px",
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="mt-3 flex gap-1.5 text-center">
                {data.map((d) => (
                    <div
                        key={d.date}
                        className="flex-1 font-mono text-[8px] text-fog-500 tracking-wide truncate"
                    >
                        {fmtDay(d.date)}
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Stacked dual bar chart: each day has an "image" portion (emerald) on
 * top and a "text" portion (periwinkle) below. The total height of the
 * stack is proportional to the day's combined value. When `isMoney` is
 * true, values are interpreted as COP and tooltips formatted as such.
 */
function StackedBarChart({ data, isMoney, granularity = "day" }) {
    const isMonth = granularity === "month";
    const max = Math.max(1, ...data.map((d) => d.image + d.text));
    const fmtDay = (iso) => {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit" });
    };
    const fmtDayShort = (iso) => {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString("es-CO", { day: "2-digit" });
    };
    const fmtMonth = (iso) => {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString("es-CO", { month: "short" }).replace(".", "");
    };
    const fmtVal = (v) =>
        isMoney
            ? `$${Number(v || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`
            : Number(v || 0).toLocaleString("es-CO");

    // Pick a tick stride so we never paint more than ~12 day labels,
    // no matter how long the range is. With 90 days every 8th label
    // gives 11-12 ticks; 30 days every 3rd gives 10; 7 days shows all.
    // We ALWAYS render the very first and the very last label so the
    // user can read the bounds of the X axis at a glance.
    const targetTicks = 12;
    const stride = Math.max(1, Math.ceil(data.length / targetTicks));
    const monthChangeIndexes = new Set();
    let prevMonth = null;
    data.forEach((d, i) => {
        const m = new Date(d.date + "T00:00:00Z").getMonth();
        if (m !== prevMonth) {
            monthChangeIndexes.add(i);
            prevMonth = m;
        }
    });
    // Precompute which indexes render a label. Walks left-to-right and
    // enforces a minimum gap of `stride` between any two visible labels
    // so a month-change tick that lands one or two cells next to a
    // stride tick doesn't overlap visually. Priority: index 0, then
    // month changes (these always win — month boundaries are the most
    // informative anchors), then regular stride ticks, then last index.
    const candidates = new Set();
    for (let i = 0; i < data.length; i++) {
        if (
            i === 0 ||
            i === data.length - 1 ||
            i % stride === 0 ||
            monthChangeIndexes.has(i)
        ) {
            candidates.add(i);
        }
    }
    const ordered = Array.from(candidates).sort((a, b) => {
        // Month changes first, then position. This guarantees they get
        // their slot before nearby stride ticks claim it.
        const aMonth = monthChangeIndexes.has(a) ? 0 : 1;
        const bMonth = monthChangeIndexes.has(b) ? 0 : 1;
        if (aMonth !== bMonth) return aMonth - bMonth;
        return a - b;
    });
    const minGap = stride;
    const visible = [];
    for (const idx of ordered) {
        if (visible.every((v) => Math.abs(v - idx) >= minGap)) {
            visible.push(idx);
        }
    }
    // Always ensure first and last are present even if collision rule
    // dropped them above.
    visible.push(0, data.length - 1);
    const visibleSet = new Set(visible);
    const shouldShowLabel = (i) => (isMonth ? true : visibleSet.has(i));

    return (
        <div
            data-testid="stats-stacked-bar-chart"
            className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5 overflow-hidden"
        >
            <div
                className="flex items-stretch gap-1.5"
                style={{ height: 200 }}
            >
                {data.map((d) => {
                    const total = d.image + d.text;
                    const imgH = total > 0 ? (d.image / max) * 100 : 0;
                    const txtH = total > 0 ? (d.text / max) * 100 : 0;
                    return (
                        <div
                            key={d.date}
                            title={`${isMonth ? d.label : fmtDay(d.date)} · imagen ${fmtVal(d.image)} · texto ${fmtVal(d.text)}`}
                            className="group flex-1 min-w-0 flex flex-col justify-end relative h-full"
                        >
                            {total === 0 ? (
                                <div className="w-full bg-ink-700 rounded-t" style={{ height: "2px" }} />
                            ) : (
                                <>
                                    {d.text > 0 && (
                                        <div
                                            className="w-full bg-periwinkle-400 group-hover:bg-periwinkle-300 transition-colors"
                                            style={{ height: `${Math.max(txtH, 1)}%` }}
                                        />
                                    )}
                                    {d.image > 0 && (
                                        <div
                                            className="w-full bg-emerald-400 group-hover:bg-emerald-300 transition-colors rounded-t"
                                            style={{ height: `${Math.max(imgH, 1)}%` }}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="mt-3 flex gap-1.5 text-center">
                {data.map((d, i) => {
                    const show = shouldShowLabel(i);
                    const isMonthChange = monthChangeIndexes.has(i) && i !== 0;
                    return (
                        <div
                            key={d.date}
                            className="flex-1 min-w-0 font-mono text-[9px] text-fog-500 tracking-wide overflow-hidden whitespace-nowrap text-center"
                        >
                            {show && (
                                <span
                                    className={
                                        !isMonth && isMonthChange
                                            ? "text-periwinkle-400"
                                            : ""
                                    }
                                >
                                    {isMonth
                                        ? d.label
                                        : isMonthChange
                                            ? `${fmtDayShort(d.date)} ${fmtMonth(d.date)}`
                                            : fmtDay(d.date)}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function UserHeatmap({ data, isMoney, granularity = "day" }) {
    const isMonth = granularity === "month";
    const valOf = (s) =>
        isMoney
            ? (s.image_cost_cop || 0) + (s.text_cost_cop || 0)
            : (s.image_count || 0) + (s.text_count || 0);
    const totalOf = (r) =>
        isMoney
            ? r.total_cost_cop || 0
            : r.total || 0;
    // When the window spans > 30 days we roll each user's per-day series
    // up into per-month columns so the table stays compact.
    let days, rows;
    if (isMonth) {
        const monthKeys = [];
        (data.days || []).forEach((d) => {
            const k = monthKeyOf(d);
            if (!monthKeys.includes(k)) monthKeys.push(k);
        });
        days = monthKeys;
        rows = (data.rows || []).map((r) => {
            const mb = {};
            r.series.forEach((s) => {
                const k = monthKeyOf(s.date);
                const cur = mb[k] || {
                    date: k,
                    image_count: 0,
                    text_count: 0,
                    image_cost_cop: 0,
                    text_cost_cop: 0,
                };
                cur.image_count += s.image_count || 0;
                cur.text_count += s.text_count || 0;
                cur.image_cost_cop += s.image_cost_cop || 0;
                cur.text_cost_cop += s.text_cost_cop || 0;
                mb[k] = cur;
            });
            return {
                ...r,
                series: monthKeys.map(
                    (k) =>
                        mb[k] || {
                            date: k,
                            image_count: 0,
                            text_count: 0,
                            image_cost_cop: 0,
                            text_cost_cop: 0,
                        },
                ),
            };
        });
    } else {
        days = data.days;
        rows = data.rows;
    }
    const max = Math.max(1, ...rows.flatMap((r) => r.series.map(valOf)));
    const fmtDay = (iso) => {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit" });
    };
    const fmtCol = (key) =>
        String(key).length === 7 ? monthLabelShort(key) : fmtDay(key);
    const fmt = (v) =>
        isMoney
            ? `$${Number(v || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`
            : Number(v || 0).toLocaleString("es-CO");
    return (
        <div
            data-testid="stats-heatmap"
            className="rounded-2xl border hairline border-ink-600 bg-ink-900 overflow-x-auto"
        >
            <table className="min-w-full text-xs">
                <thead>
                    <tr className="border-b hairline border-ink-600">
                        <th className="text-left px-4 py-3 font-mono text-[10px] tracking-widest text-fog-400 uppercase sticky left-0 bg-ink-900">
                            Usuario
                        </th>
                        {days.map((d) => (
                            <th
                                key={d}
                                className="px-2 py-3 font-mono text-[9px] tracking-wider text-fog-400 uppercase text-center"
                            >
                                {fmtCol(d)}
                            </th>
                        ))}
                        <th className="px-3 py-3 font-mono text-[10px] tracking-widest text-fog-400 uppercase text-right">
                            Total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr
                            key={r.user_id}
                            data-testid={`stats-user-row-${r.user_id}`}
                            className="border-b hairline border-ink-600 last:border-0"
                        >
                            <td className="px-4 py-2 text-fog-100 sticky left-0 bg-ink-900">
                                {r.user_name}
                            </td>
                            {r.series.map((s) => {
                                const v = valOf(s);
                                const ratio = v / max;
                                const bg =
                                    v === 0
                                        ? "transparent"
                                        : `rgba(1, 233, 189, ${Math.max(0.15, ratio).toFixed(2)})`;
                                return (
                                    <td
                                        key={s.date}
                                        title={`${s.date}: imagen ${fmt(isMoney ? s.image_cost_cop : s.image_count)} · texto ${fmt(isMoney ? s.text_cost_cop : s.text_count)}`}
                                        className="px-1.5 py-1.5 text-center"
                                    >
                                        <div
                                            className={`w-8 h-8 mx-auto rounded-md flex items-center justify-center text-[11px] font-mono ${
                                                v > 0 ? "text-ink-950" : "text-fog-500"
                                            }`}
                                            style={{
                                                background: bg,
                                                border:
                                                    v > 0
                                                        ? "1px solid rgba(1,233,189,0.5)"
                                                        : "1px solid rgba(10,14,42,0.05)",
                                            }}
                                        >
                                            {v > 0
                                                ? isMoney
                                                    ? "$"
                                                    : v
                                                : "·"}
                                        </div>
                                    </td>
                                );
                            })}
                            <td className="px-3 py-2 font-display text-periwinkle-500 text-right">
                                {fmt(totalOf(r))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Leaderboard({ title, rows, labelKey, color, testid, isMoney }) {
    const valOf = (r) => (isMoney ? r.cost_cop || 0 : r.count || r.images || 0);
    const max = Math.max(1, ...rows.map(valOf));
    const fmt = (v) =>
        isMoney
            ? `$${Number(v || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`
            : Number(v || 0).toLocaleString("es-CO");
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5"
        >
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-3">
                {title}
            </p>
            {rows.length === 0 && (
                <p className="text-fog-500 text-xs font-mono py-6 text-center">
                    Sin datos.
                </p>
            )}
            <ul className="space-y-3">
                {rows.slice(0, 6).map((r, i) => {
                    const n = valOf(r);
                    const ratio = (n / max) * 100;
                    const label =
                        r[labelKey] ||
                        r.name ||
                        r.user_name ||
                        r._id ||
                        r.brand_name ||
                        "—";
                    // Show a tiny "img / text" split below each row so
                    // admins see the mix at a glance.
                    const imgCount = r.image_count;
                    const txtCount = r.text_count;
                    const showSplit =
                        (imgCount != null && txtCount != null) &&
                        (imgCount > 0 || txtCount > 0);
                    return (
                        <li key={i} className="flex items-start gap-3">
                            <span className="font-mono text-[10px] text-fog-500 w-5 shrink-0 text-right pt-0.5">
                                {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline gap-2">
                                    <span
                                        className="text-fog-100 text-sm truncate"
                                        title={label}
                                    >
                                        {label}
                                    </span>
                                    <span
                                        className="font-display text-base shrink-0 tabular-nums"
                                        style={{ color }}
                                    >
                                        {fmt(n)}
                                    </span>
                                </div>
                                {showSplit && (
                                    <div className="font-mono text-[9px] text-fog-500 mt-0.5 flex gap-2">
                                        <span className="text-emerald-400">{imgCount}img</span>
                                        <span className="text-periwinkle-400">{txtCount}txt</span>
                                    </div>
                                )}
                                <div className="h-1 rounded-full bg-ink-700 overflow-hidden mt-1.5">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${ratio}%`,
                                            background: color,
                                        }}
                                    />
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
